import { ANALYTICS_ACTION_CHANGED, ANALYTICS_ACTION_CLICKED, ANALYTICS_ACTION_COLLAPSED, ANALYTICS_ACTION_EXPANDED, ANALYTICS_ACTION_FETCH_FAILED, ANALYTICS_ACTION_REMOVE_FAILED, ANALYTICS_ACTION_REMOVED, ANALYTICS_SUBJECT_APP, ANALYTICS_SUBJECT_BUTTON, ANALYTICS_SUBJECT_UI, BreadcrumbClickedEvent, ButtonClickedEvent, FilterClickedEvent, ImageClickedEvent, LinkClickedEvent, makeAnalyticsAction, makeAnalyticsActionSubject, makeAnalyticsCategory, makeEventContainer, MenuItemClickedEvent, OPERATIONAL_EVENT_TYPE, SearchSubmittedEvent, TabClickedEvent, TileClickedEvent, TRACK_EVENT_TYPE, UI_EVENT_TYPE } from '@atlassian/mpac-analytics';
import { Set } from 'immutable';
// ⚠️ IMPORTANT!! ⚠️
//
// To add a new analytics event, you MUST:
// 1) Add a const below representing the event's action, actionSubject, details and type
// 2) Add the corresponding type to AnalyticsEvent
// 3) Add the const from step 1 to allAnalyticsEvents
//
// If you skip any step, you will get an error at runtime, because the event will not be registered
// in Data Portal during the deployment process.

// Analytics Containers
export const ANALYTICS_EVENT_CONTAINER_NAV_BAR = makeEventContainer('emceeNavBar');
export const ANALYTICS_EVENT_CONTAINER_EMCEE_OVERVIEW_TAB = makeEventContainer('emceeOverviewTab');

// Analytics Actions
const ANALYTICS_ACTION_GENERATED = makeAnalyticsAction('generated');
const ANALYTICS_ACTION_GENERATE_FAILED = makeAnalyticsAction('generateFailed');
const ANALYTICS_ACTION_INSTALLED = makeAnalyticsAction('installed');
const ANALYTICS_ACTION_INSTALL_FAILED = makeAnalyticsAction('installFailed');
const ANALYTICS_ACTION_UPGRADED = makeAnalyticsAction('upgraded');
const ANALYTICS_ACTION_UPGRADE_FAILED = makeAnalyticsAction('upgradeFailed');
const ANALYTICS_ACTION_REGENERATED = makeAnalyticsAction('regenerated');
const ANALYTICS_ACTION_REGENERATE_FAILED = makeAnalyticsAction('regenerateFailed');
const ANALYTICS_ACTION_FIRST_MEANINGFUL_PAINT_COMPLETED = makeAnalyticsAction('firstMeaningfulPaintCompleted');
const ANALYTICS_ACTION_SORTED = makeAnalyticsAction('sorted');
const ANALYTICS_ACTION_APP_REQUESTED = makeAnalyticsAction('requested');
const ANALYTICS_ACTION_APP_REQUEST_FAILED = makeAnalyticsAction('requestFailed');
const ANALYTICS_ACTION_APP_PROMOTION_REDEEMED_FAILED = makeAnalyticsAction('redeemPromotionFailed');
const ANALYTICS_ACTION_APP_PROMOTION_REDEEMED = makeAnalyticsAction('promotionRedeemed');
const ANALYTICS_ACTION_APP_PROMOTION_VALIDATED = makeAnalyticsAction('promotionValidated');
const ANALYTICS_ACTION_APP_REQUEST_BUTTON_VIEWED = makeAnalyticsAction('requestButtonViewed');
const ANALYTICS_ACTION_APP_INSTALL_PERMISSION_EVALUATION_FAILED = makeAnalyticsAction('installPermissionEvaluationFailed');
const ANALYTICS_ACTION_FEATURE_EXPOSED = makeAnalyticsAction('exposed');

// Analytics Action Subjects
const ANALYTICS_SUBJECT_EXPANDABLE_SECTION = makeAnalyticsActionSubject('expandableSection');
const ANALYTICS_SUBJECT_TOKEN = makeAnalyticsActionSubject('token');
const ANALYTICS_SUBJECT_USER_GRANT = makeAnalyticsActionSubject('userGrant');
const ANALYTICS_SUBJECT_DYNAMIC_TABLE = makeAnalyticsActionSubject('dynamicTable');
const ANALYTICS_SUBJECT_FEATURE = makeAnalyticsActionSubject('feature');

// Analytics Action Subject IDs
export const ANALYTICS_SUBJECT_ID_ALREADY_INSTALLED_CTA = 'alreadyInstalledCTA';
export const ANALYTICS_SUBJECT_ID_DISCOVER = 'discover';
export const ANALYTICS_SUBJECT_ID_MANAGE = 'manage';
export const ANALYTICS_SUBJECT_ID_APP_NAME = 'appName';
export const ANALYTICS_SUBJECT_ID_BYO_TILE = 'byoTile';
export const ANALYTICS_SUBJECT_ID_CONTEXT_NAME = 'contextName';
export const ANALYTICS_SUBJECT_ID_FLAG_ACTION = 'flagAction';
export const ANALYTICS_SUBJECT_ID_INSTALL = 'install';
export const ANALYTICS_SUBJECT_ID_INSTALL_CANCEL = 'installCancel';
export const ANALYTICS_SUBJECT_ID_INSTALL_CLOSE = 'installClose';
export const ANALYTICS_SUBJECT_ID_TOKEN_DONE = 'tokenDone';
export const ANALYTICS_SUBJECT_ID_UPGRADE = 'upgrade';
export const ANALYTICS_SUBJECT_ID_LEARN_MORE = 'learnMore';
export const ANALYTICS_SUBJECT_ID_CREATE_TOKEN = 'createToken';
export const ANALYTICS_SUBJECT_ID_MANAGE_ACTION_CANCEL = 'manageActionCancel';
export const ANALYTICS_SUBJECT_ID_MANAGE_ACTION_CONFIRM = 'manageActionConfirm';
export const ANALYTICS_SUBJECT_ID_MANAGE_APPS_ACTION = 'manageAppsAction';
export const ANALYTICS_SUBJECT_ID_MANAGE_CONFIGURE = 'manageConfigure';
export const ANALYTICS_SUBJECT_ID_MANAGE_MENU_OPEN = 'manageMenuOpen';
export const ANALYTICS_SUBJECT_ID_MORE_RESULTS = 'moreResults';
export const ANALYTICS_SUBJECT_ID_NAV_BAR_CUSTOM_APP = 'navBarCustomApp';
export const ANALYTICS_SUBJECT_ID_NAV_BAR_VIEW_SWITCHER = 'navBarViewSwitcher';
export const ANALYTICS_SUBJECT_ID_PRIVACY_POLICY = 'privacyPolicy';
export const ANALYTICS_SUBJECT_ID_REVOKE_USER_GRANT = 'revokeUserGrant';
export const ANALYTICS_SUBJECT_ID_TERMS_OF_USE = 'termsOfUse';
export const ANALYTICS_SUBJECT_ID_MANAGE_LOG_ACCESS = 'manageLogAccess';
export const ANALYTICS_SUBJECT_ID_MARKETPLACE_TERMS_OF_USE = 'marketplaceTermsOfUse';
export const ANALYTICS_SUBJECT_ID_USER_GRANT_DETAILS = 'userGrantDetails';
export const ANALYTICS_SUBJECT_ID_VIEW_APP_DETAILS = 'viewAppDetails';
export const ANALYTICS_SUBJECT_ID_VIEW_APP_PRICING_DETAILS = 'viewAppPricingDetails';
export const ANALYTICS_SUBJECT_ID_PARTNER_NAME = 'partnerName';
export const ANALYTICS_SUBJECT_ID_DISCOVER_APP_LISTING = 'discoverAppListing';
export const ANALYTICS_SUBJECT_ID_PERF_METRICS = 'perfMetrics';
export const ANALYTICS_SUBJECT_ID_APP_INSTALL_DETERMINING_MODAL = 'installDeterminingModal';
export const ANALYTICS_SUBJECT_ID_APP_INSTALL_MODAL = 'installModal';
export const ANALYTICS_SUBJECT_ID_VIEW_MORE_ON_MARKETPLACE_LINK = 'viewMoreOnMarketplaceLink';
export const ANALYTICS_SUBJECT_ID_FEEDBACK_CLICKED_BUTTON = 'emceeFeedbackButton';
export const ANALYTICS_SUBJECT_ID_FEEDBACK_SUBMIT_BUTTON = 'emceeFeedbackSubmitButton';

// APP LISTING
export const ANALYTICS_OBJECT_TYPE_APP_LISTING = 'appListing';
export const ANALYTICS_SUBJECT_ID_EMCEE_APP_LISTING_TAB = 'emceeAppListingTab';

// APP REQUESTS
export const ANALYTICS_OBJECT_TYPE_APP_REQUESTS = 'appRequests';
export const ANALYTICS_SUBJECT_ID_MANAGE_APP_REQUESTS = 'manageAppRequests';
export const ANALYTICS_SUBJECT_ID_REJECT_REQUEST = 'rejectRequest';
export const ANALYTICS_SUBJECT_ID_APPROVE_REQUEST = 'approveRequest';
export const ANALYTICS_SUBJECT_ID_REQUEST_APP = 'requestApp';
export const ANALYTICS_SUBJECT_ID_REQUEST_APP_CANCELED = 'requestAppCancelled';
export const ANALYTICS_SUBJECT_ID_REQUEST_APP_SUBMITTED = 'requestAppSubmitted';
export const ANALYTICS_SUBJECT_ID_REJECT_REQUEST_CANCEL = 'rejectRequestCancelled';
export const ANALYTICS_SUBJECT_ID_REJECT_REQUEST_SUBMIT = 'rejectRequestSubmitted';
export const ANALYTICS_SUBJECT_ID_APP_REQUEST_COMMENTS_CANCEL = 'appRequestCommentsCancel';
export const ANALYTICS_SUBJECT_ID_APP_REQUEST_COMMENTS_VIEW_APP_DETAILS = 'viewAppDetails';
export const ANALYTICS_SUBJECT_ID_APP_REQUEST_COUNT = 'requestCount';
export const ANALYTICS_SUBJECT_ID_MANAGE_APP_REQUESTS_FILTER = 'manageAppRequestsFilter';
export const ANALYTICS_SUBJECT_ID_MANAGE_APP_REQUESTS_TABLE = 'manageAppRequestsTable';
export const ANALYTICS_SUBJECT_ID_MANAGE_APP_REQUESTS_SEARCH = 'manageAppRequestsSearch';
export const ANALYTICS_SUBJECT_ID_MANAGE_APP_REQUESTS_PAGE = 'manageAppRequestsPage';
export const ANALYTICS_SUBJECT_ID_RUSSIAN_USER_BLOCKED = 'blockedRussianUser';

// App Promotions
export const ANALYTICS_OBJECT_TYPE_APP_PROMOTIONS = 'appPromotions';
export const ANALYTICS_SUBJECT_ID_MANAGE_APP_PROMOTIONS = 'manageAppPromotions';
export const ANALYTICS_SUBJECT_ID_MANAGE_APP_PROMOTIONS_FILTER = 'manageAppPromotionsFilter';
export const ANALYTICS_SUBJECT_ID_TOGGLE_APPS_IN_PROMOTION = 'toggleAppsInPromotion';
export const ANALYTICS_SUBJECT_ID_APPLY_PROMOTION = 'applyPromotion';
export const ANALYTICS_SUBJECT_ID_EMPTY_VIEW_APPLY_PROMOTION = 'emptyViewApplyPromotion';
export const ANALYTICS_SUBJECT_ID_MISSING_INSTALLED_APPS = 'missingInstalledApps';
export const ANALYTICS_SUBJECT_ID_CANCEL_APPLY_PROMOTION_MODAL = 'cancelApplyPromotionModal';

// Analytics Category Names
export const ANALYTICS_CATEGORY_INSTALL = makeAnalyticsCategory('EmceeInstall');
export const ANALYTICS_CATEGORY_UNINSTALL = makeAnalyticsCategory('EmceeUninstall');
export const ANALYTICS_CATEGORY_TOKENS = makeAnalyticsCategory('EmceeTokens');
export const ANALYTICS_CATEGORY_NAVIGATION = makeAnalyticsCategory('EmceeNavigation');
export const ANALYTICS_CATEGORY_MARKETPLACE_APP_INSTALL = makeAnalyticsCategory('MarketplaceAppInstall');
export const ANALYTICS_CATEGORY_MARKETPLACE_APP_REQUEST = makeAnalyticsCategory('MarketplaceAppRequest');
export const FeatureExposedEvent = {
  action: ANALYTICS_ACTION_FEATURE_EXPOSED,
  actionSubject: ANALYTICS_SUBJECT_FEATURE,
  details: 'Feature exposed to user',
  type: TRACK_EVENT_TYPE
};
export const AppInstalledEvent = {
  action: ANALYTICS_ACTION_INSTALLED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'App installation succeeds',
  type: TRACK_EVENT_TYPE
};
export const AppInstallFailedEvent = {
  action: ANALYTICS_ACTION_INSTALL_FAILED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'Error occurs while installing an app',
  type: OPERATIONAL_EVENT_TYPE
};
export const AppUpgradedEvent = {
  action: ANALYTICS_ACTION_UPGRADED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'User upgrades an app in Embedded Marketplace',
  type: TRACK_EVENT_TYPE
};
export const AppUpgradeFailedEvent = {
  action: ANALYTICS_ACTION_UPGRADE_FAILED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'Error occurs while upgrading an app in Embedded Marketplace',
  type: OPERATIONAL_EVENT_TYPE
};
export const AppRemovedEvent = {
  action: ANALYTICS_ACTION_REMOVED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'User uninstalls an app in Embedded Marketplace',
  type: TRACK_EVENT_TYPE
};
export const AppRemoveFailedEvent = {
  action: ANALYTICS_ACTION_REMOVE_FAILED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'Error occurs while uninstalling an app through Embedded Marketplace',
  type: OPERATIONAL_EVENT_TYPE
};
export const ExpandableSectionCollapsedEvent = {
  action: ANALYTICS_ACTION_COLLAPSED,
  actionSubject: ANALYTICS_SUBJECT_EXPANDABLE_SECTION,
  details: 'User collapsed an expandable section',
  type: UI_EVENT_TYPE
};
export const ExpandableSectionExpandedEvent = {
  action: ANALYTICS_ACTION_EXPANDED,
  actionSubject: ANALYTICS_SUBJECT_EXPANDABLE_SECTION,
  details: 'User expanded an expandable section',
  type: UI_EVENT_TYPE
};
export const FetchAppDetailsFailedEvent = {
  action: ANALYTICS_ACTION_FETCH_FAILED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'Error occurs while fetching app details in Embedded Marketplace install dialog',
  type: OPERATIONAL_EVENT_TYPE
};
export const TokenGeneratedEvent = {
  action: ANALYTICS_ACTION_GENERATED,
  actionSubject: ANALYTICS_SUBJECT_TOKEN,
  details: 'A token was generated',
  type: TRACK_EVENT_TYPE
};
export const TokenGenerateFailedEvent = {
  action: ANALYTICS_ACTION_GENERATE_FAILED,
  actionSubject: ANALYTICS_SUBJECT_TOKEN,
  details: 'An error occurred while generating a token',
  type: OPERATIONAL_EVENT_TYPE
};
export const TokenRegenerateFailedEvent = {
  action: ANALYTICS_ACTION_REGENERATE_FAILED,
  actionSubject: ANALYTICS_SUBJECT_TOKEN,
  details: 'An error occurred while regenerating a token',
  type: OPERATIONAL_EVENT_TYPE
};
export const TokenRemovedEvent = {
  action: ANALYTICS_ACTION_REMOVED,
  actionSubject: ANALYTICS_SUBJECT_TOKEN,
  details: 'A token was removed/revoked',
  type: TRACK_EVENT_TYPE
};
export const TokenRemoveFailedEvent = {
  action: ANALYTICS_ACTION_REMOVE_FAILED,
  actionSubject: ANALYTICS_SUBJECT_TOKEN,
  details: 'An error occurred while removing a token',
  type: OPERATIONAL_EVENT_TYPE
};
export const TokenRegeneratedEvent = {
  action: ANALYTICS_ACTION_REGENERATED,
  actionSubject: ANALYTICS_SUBJECT_TOKEN,
  details: 'A token was regenerated',
  type: TRACK_EVENT_TYPE
};
export const UserGrantRemovedEvent = {
  action: ANALYTICS_ACTION_REMOVED,
  actionSubject: ANALYTICS_SUBJECT_USER_GRANT,
  details: 'A user grant was successfully revoked',
  type: TRACK_EVENT_TYPE
};
export const UserGrantRemoveFailedEvent = {
  action: ANALYTICS_ACTION_REMOVE_FAILED,
  actionSubject: ANALYTICS_SUBJECT_USER_GRANT,
  details: 'An error occurred while revoking a user grant',
  type: OPERATIONAL_EVENT_TYPE
};
export const TimeToFirstMeaningfulPaintEvent = {
  action: ANALYTICS_ACTION_FIRST_MEANINGFUL_PAINT_COMPLETED,
  actionSubject: ANALYTICS_SUBJECT_UI,
  details: 'First meaningful paint completed',
  type: OPERATIONAL_EVENT_TYPE
};
export const DynamicTableSorted = {
  action: ANALYTICS_ACTION_SORTED,
  actionSubject: ANALYTICS_SUBJECT_DYNAMIC_TABLE,
  details: 'When dynamic table is sorted',
  type: UI_EVENT_TYPE
};
export const DynamicTablePageChanged = {
  action: ANALYTICS_ACTION_CHANGED,
  actionSubject: ANALYTICS_SUBJECT_DYNAMIC_TABLE,
  details: 'Page number changed in a dynamic table',
  type: UI_EVENT_TYPE
};
export const AppRequestedEvent = {
  action: ANALYTICS_ACTION_APP_REQUESTED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'End user requests admin to install an app',
  type: TRACK_EVENT_TYPE
};
export const AppRequestFailedEvent = {
  action: ANALYTICS_ACTION_APP_REQUEST_FAILED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'Error occurs while end user requesting admin to install an app',
  type: OPERATIONAL_EVENT_TYPE
};
export const AppPromotionValidated = {
  action: ANALYTICS_ACTION_APP_PROMOTION_VALIDATED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'When user validated a promo code successfully',
  type: TRACK_EVENT_TYPE
};
export const AppPromotionRedeemed = {
  action: ANALYTICS_ACTION_APP_PROMOTION_REDEEMED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'When user redeemed a promo code successfully',
  type: TRACK_EVENT_TYPE
};
export const AppPromotionRedeemFailedEvent = {
  action: ANALYTICS_ACTION_APP_PROMOTION_REDEEMED_FAILED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'Error occurs while redeeming promotion code',
  type: OPERATIONAL_EVENT_TYPE
};
export const AppInstallPermissionEvaluationFailed = {
  action: ANALYTICS_ACTION_APP_INSTALL_PERMISSION_EVALUATION_FAILED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'When app install permission evaluation is failed',
  type: OPERATIONAL_EVENT_TYPE
};
export const AppRequestButtonViewed = {
  action: ANALYTICS_ACTION_APP_REQUEST_BUTTON_VIEWED,
  actionSubject: ANALYTICS_SUBJECT_APP,
  details: 'When app request button is viewed',
  type: TRACK_EVENT_TYPE
};
export const AppInstallBlockedForRussianUserEvent = {
  action: ANALYTICS_ACTION_CLICKED,
  actionSubject: ANALYTICS_SUBJECT_BUTTON,
  details: 'Prevented app install for Russia',
  type: UI_EVENT_TYPE
};
export const allAnalyticsEvents = Set([AppInstalledEvent, AppInstallFailedEvent, AppUpgradedEvent, AppUpgradeFailedEvent, AppRemovedEvent, AppRemoveFailedEvent, DynamicTablePageChanged, BreadcrumbClickedEvent, ButtonClickedEvent, ImageClickedEvent, ExpandableSectionCollapsedEvent, ExpandableSectionExpandedEvent, FeatureExposedEvent, FetchAppDetailsFailedEvent, MenuItemClickedEvent, TileClickedEvent, TokenGeneratedEvent, TokenGenerateFailedEvent, TokenRegeneratedEvent, TokenRegenerateFailedEvent, TokenRemovedEvent, TokenRemoveFailedEvent, UserGrantRemovedEvent, UserGrantRemoveFailedEvent, TimeToFirstMeaningfulPaintEvent, DynamicTableSorted, SearchSubmittedEvent, FilterClickedEvent, AppRequestedEvent, AppRequestFailedEvent, AppRequestButtonViewed, AppInstallPermissionEvaluationFailed, AppPromotionValidated, AppPromotionRedeemed, AppPromotionRedeemFailedEvent, AppInstallBlockedForRussianUserEvent, TabClickedEvent, LinkClickedEvent]);